import ArrowDown from "@mui/icons-material/ArrowDownward";
import ArrowUp from "@mui/icons-material/ArrowUpward";
import CancelIcon from "@mui/icons-material/Cancel";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LinkIcon from "@mui/icons-material/Link";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { sortAndSwap } from "core/utils/sortAndSwap";
import { viewTransition } from "core/utils/viewTransition";
import type { DocNodeBasicSummaryDto } from "features/DMS/entities";
import type { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { isCustomLinkValid } from "./isCustomLinkValid";
import type {
  CustomLinkFormData,
  QuickLinkWidgetFormValues,
  SelectedQuickLink,
} from "./QuickLinkDefs";

export type OrderedEntry =
  | {
      type: "DocNode";
      node: DocNodeBasicSummaryDto;
      parents: DocNodeBasicSummaryDto[];
    }
  | {
      type: "CustomLink";
      quickLink: SelectedQuickLink;
    };

function ParentBreadcrumbs({
  parents,
}: {
  parents: { id: string; label: string }[];
}) {
  return (
    <Breadcrumbs separator=">">
      {parents.map(({ label, id: path }) => (
        <Typography sx={{ lineHeight: 1 }} key={path} variant="overline">
          {label}
        </Typography>
      ))}
    </Breadcrumbs>
  );
}

export function getCustomLinkDisplayName(
  customLink: CustomLinkFormData | undefined,
  locale: string
): string {
  if (!customLink) return "";
  const localeTranslation = customLink.translations.find(
    (tr) => tr.locale === locale
  );
  if (localeTranslation?.name) return localeTranslation.name;
  return customLink.translations[0]?.name ?? "";
}

function getCustomLinkDisplayUrl(
  customLink: CustomLinkFormData | undefined,
  locale: string
): string {
  if (!customLink) return "";
  const localeTranslation = customLink.translations.find(
    (tr) => tr.locale === locale
  );
  if (localeTranslation?.url) return localeTranslation.url;
  return customLink.translations[0]?.url ?? "";
}

export function SelectedQuickLinksList({
  orderedEntries,
  selectedQuickLinkIds,
  formMethods,
  locale,
  maxSelections,
}: {
  orderedEntries: OrderedEntry[];
  selectedQuickLinkIds: string[];
  formMethods: ReturnType<typeof useForm<QuickLinkWidgetFormValues>>;
  locale: string;
  maxSelections: number;
}) {
  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:QuickLinksWidgetSettingsDialog",
  });

  function handleRemove(id: string) {
    const current = formMethods.getValues("selectedQuickLinks");
    const currentOrder = formMethods.getValues("quickLinkOrder");
    viewTransition(() => {
      formMethods.setValue(
        "selectedQuickLinks",
        current.filter((item) => item.id !== id)
      );
      formMethods.setValue(
        "quickLinkOrder",
        currentOrder.filter((itemId) => itemId !== id)
      );
    });
  }

  function setOrder(setFun: (currentOrder: string[]) => string[]) {
    const currentOrder = formMethods.getValues("quickLinkOrder");
    const newOrder = setFun(currentOrder);
    viewTransition(() => {
      formMethods.setValue("quickLinkOrder", newOrder);
    });
  }

  function handleUp(index: number) {
    setOrder((currentOrder) =>
      sortAndSwap(selectedQuickLinkIds, currentOrder, index, index - 1)
    );
  }

  function handleDown(index: number) {
    setOrder((currentOrder) =>
      sortAndSwap(selectedQuickLinkIds, currentOrder, index, index + 1)
    );
  }

  return (
    <Box flex={1}>
      <Typography my={2}>
        {t("selectedLinks", {
          defaultValue: "Selected Quick Links {{selectedCount}}/{{max}}",
          selectedCount: selectedQuickLinkIds.length,
          max: maxSelections,
        })}
      </Typography>
      <Stack
        sx={{
          "& > *": { viewTransitionName: "match-element" },
        }}
        gap={1}
      >
        {orderedEntries.length > 0 ? (
          orderedEntries.map((entry, index, { length }) => {
            if (entry.type === "DocNode") {
              return (
                <Box
                  component={Paper}
                  variant="outlined"
                  key={entry.node.id}
                  display="flex"
                  justifyContent="space-between"
                  p={1}
                  gap={2}
                >
                  <Stack>
                    <ParentBreadcrumbs
                      parents={entry.parents.map((parent) => ({
                        id: parent.id!,
                        label: parent.name!,
                      }))}
                    />
                    <Typography>{entry.node.name!}</Typography>
                  </Stack>
                  <ButtonGroup
                    orientation="horizontal"
                    sx={{
                      "& > button": { flexGrow: 1 },
                    }}
                    size="small"
                  >
                    <Button
                      variant="outlined"
                      disabled={index === 0}
                      onClick={() => handleUp(index)}
                    >
                      <ArrowUp />
                    </Button>
                    <Button
                      variant="outlined"
                      disabled={index >= length - 1}
                      onClick={() => handleDown(index)}
                    >
                      <ArrowDown />
                    </Button>
                    <Button
                      color="warning"
                      onClick={() => handleRemove(entry.node.path!)}
                    >
                      <CancelIcon />
                    </Button>
                  </ButtonGroup>
                </Box>
              );
            }
            const cl = entry.quickLink;
            const valid = isCustomLinkValid(cl.customLink);
            return (
              <Box
                component={Paper}
                variant="outlined"
                key={cl.id}
                display="flex"
                justifyContent="space-between"
                p={1}
                gap={2}
                sx={valid ? undefined : { borderColor: "error.main" }}
              >
                <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
                  <LinkIcon fontSize="small" color="action" />
                  <Stack minWidth={0}>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Typography noWrap>
                        {getCustomLinkDisplayName(cl.customLink, locale) ||
                          t("unnamedLink", "Unnamed link")}
                      </Typography>
                      <Chip
                        label={t("customLinkChip", "Link")}
                        size="small"
                        variant="outlined"
                      />
                      {!valid && (
                        <Tooltip
                          title={t(
                            "incompleteTooltip",
                            "Missing translations — all locales must have a name and URL."
                          )}
                        >
                          <ErrorOutlineIcon fontSize="small" color="error" />
                        </Tooltip>
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {getCustomLinkDisplayUrl(cl.customLink, locale)}
                    </Typography>
                  </Stack>
                </Stack>
                <ButtonGroup
                  orientation="horizontal"
                  sx={{
                    "& > button": { flexGrow: 1 },
                  }}
                  size="small"
                >
                  <Button
                    variant="outlined"
                    disabled={index === 0}
                    onClick={() => handleUp(index)}
                  >
                    <ArrowUp />
                  </Button>
                  <Button
                    variant="outlined"
                    disabled={index >= length - 1}
                    onClick={() => handleDown(index)}
                  >
                    <ArrowDown />
                  </Button>
                  <Button color="warning" onClick={() => handleRemove(cl.id)}>
                    <CancelIcon />
                  </Button>
                </ButtonGroup>
              </Box>
            );
          })
        ) : (
          <Typography>
            {t(
              "noSelectionHelperText",
              "Select documents or add links to include as quick links."
            )}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
